import React, {
  useEffect,
  useState
} from 'react';

import {
  Box,
  Typography,
  Button,
  Stack
} from '@mui/material';

import GridLayout from '../components/layout/GridLayout';
import Menu from '../components/layout/Menu';
import Card from '../components/ui/Card';

import { useAuth } from '../context/AuthContext';

import { supabase } from '../lib/supabase';

interface MyPageProps {
  isMenuOpen?: boolean;
}

interface Profile {
  id: string;
  email: string;
  plan: string;
  sessions_completed: number;
  created_at: string;
}

export default function MyPage({
  isMenuOpen = false
}: MyPageProps) {

  const { user } = useAuth();

  const [profile, setProfile] =
    useState<Profile | null>(null);

  const [loading, setLoading] =
    useState(true);

  // ========================================
  // FETCH PROFILE
  // ========================================

  useEffect(() => {

    const fetchProfile = async () => {

      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } =
        await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

      if (!error && data) {
        setProfile(data);
      }

      setLoading(false);
    };

    fetchProfile();

  }, [user]);

  // ========================================
  // ZOOM SESSION
  // ========================================

  const zoomMeeting = {
    title: 'Pilates Flow',
    date: '2026-05-12',
    startTime: '12:15',
    endTime: '12:45',
    displayTime:
      '12:15 pm – 12:45 pm (CNX, Thailand)',

    meetingId: '849 9781 4024',

    password: '0kEpQA',

    link:
      'https://us05web.zoom.us/j/84997814024?pwd=0Z8haDjvCDwptvCarGRGyjGSoqbc80.1'
  };

  // ========================================
  // GOOGLE CALENDAR FORMAT
  // ========================================

  const formatDate = (
    date: string,
    time: string
  ) => {

    return new Date(
      `${date}T${time}:00+07:00`
    )
      .toISOString()
      .replace(/[-:]/g, '')
      .split('.')[0] + 'Z';
  };

  const startUTC = formatDate(
    zoomMeeting.date,
    zoomMeeting.startTime
  );

  const endUTC = formatDate(
    zoomMeeting.date,
    zoomMeeting.endTime
  );

  const googleCalendarUrl =
    `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      zoomMeeting.title
    )}&dates=${startUTC}/${endUTC}&details=${encodeURIComponent(
      `Join Zoom: ${zoomMeeting.link}`
    )}&location=Online`;

  // ========================================
  // UI
  // ========================================

  return (

    <GridLayout

      isMenuOpen={isMenuOpen}

      left={<Menu />}

      main={

        <Stack spacing={3}>

          {/* ================================= */}
          {/* PROFILE CARD */}
          {/* ================================= */}

          <Card>

            <Typography
              variant="h4"
              sx={{
                color: '#34de9aab',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              My Page
            </Typography>

            <Typography variant="body1">

              Welcome back

              {' '}

              {profile?.email || user?.email}

            </Typography>

            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 2
              }}
            >

              {loading ? (

                <Typography>
                  Loading profile...
                </Typography>

              ) : (

                <>

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Email:
                    {' '}
                    {profile?.email || '-'}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Date Joined:
                    {' '}

                    {profile?.created_at
                      ? new Date(
                          profile.created_at
                        ).toLocaleDateString()
                      : '-'}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Active Plan:
                    {' '}
                    {profile?.plan || '-'}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                  >
                    Sessions Completed:
                    {' '}

                    {profile?.sessions_completed ?? 0}
                  </Typography>

                </>

              )}

            </Box>

          </Card>

          {/* ================================= */}
          {/* LIVE SESSION CARD */}
          {/* ================================= */}

          <Card
            sx={{
              border: '1px solid #eee',
              borderRadius: 3,
              p: 3
            }}
          >

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Upcoming Live Session
              {' '}
              — opens 10 minutes before start
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#34de9aab',
                fontWeight: 'bold'
              }}
            >
              {zoomMeeting.title}
            </Typography>

            <Typography sx={{ mt: 1 }}>

              {zoomMeeting.date}

              {' — '}

              {zoomMeeting.displayTime}

            </Typography>

            <Box sx={{ mt: 2 }}>

              <Typography variant="body2">
                Meeting ID:
                {' '}
                {zoomMeeting.meetingId}
              </Typography>

              <Typography variant="body2">
                Password:
                {' '}
                {zoomMeeting.password}
              </Typography>

            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 3 }}
            >

              <Button
                variant="contained"
                href={zoomMeeting.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: '#34de9aab',

                  '&:hover': {
                    bgcolor: '#2bc48a'
                  }
                }}
              >
                Join Session
              </Button>

              <Button
                variant="outlined"
                href={googleCalendarUrl}
                target="_blank"
                sx={{
                  borderColor: '#34de9aab',
                  color: '#34de9aab'
                }}
              >
                Add to Calendar
              </Button>

            </Stack>

          </Card>

        </Stack>
      }

      right={

        <Card>

          <Typography
            variant="h6"
            sx={{
              color: '#34de9aab',
              mb: 1
            }}
          >
            AI Health Stats
          </Typography>

          <Box
            sx={{
              mt: 2,
              height: 150,
              bgcolor: '#e0e0e0',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >

            <Typography variant="caption">
              Chart Loading...
            </Typography>

          </Box>

        </Card>
      }
    />
  );
}